import { ROUTES } from './constants';
import {
  ACCOUNTING_APP_ID,
  BILLS_APP_ID,
  CAPITALS_APP_ID,
  CASH_DISBURSEMENTS_JOURNAL_APP_ID,
  CASH_RECEIPTS_JOURNAL_APP_ID,
  CHART_OF_ACCOUNTS_APP_ID,
  CUSTOM_ENTRIES_APP_ID,
  CUSTOMERS_APP_ID,
  FINANCIAL_STATEMENTS_APP_ID,
  GENERAL_LEDGER_APP_ID,
  INVOICES_APP_ID,
  JOURNALS_APP_ID,
  LEDGER_REPORTS_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
  NON_VAT_BOOKS_APP_ID,
  PPE_APP_ID,
  POS_APP_ID,
  REPORTS_APP_ID,
  SUPPLIERS_APP_ID,
  TAX_CALENDAR_APP_ID,
  UPLOAD_ENTRY_APP_ID,
  isAnySlimBooksAppInstalled,
} from './installedAppPathScope';

export type DownloadablesVisibility = {
  showPosDownloads: boolean;
  showAccountingDownloads: boolean;
  showTaxDownloads: boolean;
  hasVisibleDownloads: boolean;
};

type DownloadableAppAccess = {
  appIds: readonly string[];
  path: string;
};

const BOOKS_BUNDLE_APP_IDS = [
  NON_VAT_BOOKS_APP_ID,
  NON_VAT_ACCRUAL_BOOKS_APP_ID,
] as const;

const JOURNAL_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  JOURNALS_APP_ID,
  ...BOOKS_BUNDLE_APP_IDS,
] as const;

const REPORT_WORKSPACE_APP_IDS = [
  ACCOUNTING_APP_ID,
  REPORTS_APP_ID,
  ...BOOKS_BUNDLE_APP_IDS,
] as const;

/** Installed app → route the user must be able to open for that download. */
const POS_DOWNLOAD_ACCESS: DownloadableAppAccess = {
  appIds: [POS_APP_ID],
  path: ROUTES.POS.ROOT,
};

const TAX_DOWNLOAD_ACCESS: DownloadableAppAccess = {
  appIds: [TAX_CALENDAR_APP_ID],
  path: ROUTES.TAX_COMPLIANCE,
};

const ACCOUNTING_DOWNLOAD_ACCESS: readonly DownloadableAppAccess[] = [
  {
    appIds: JOURNAL_WORKSPACE_APP_IDS,
    path: ROUTES.JOURNALS.ROOT,
  },
  {
    appIds: REPORT_WORKSPACE_APP_IDS,
    path: ROUTES.REPORTS.ROOT,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CASH_RECEIPTS_JOURNAL_APP_ID],
    path: ROUTES.CASH_RECEIPTS_JOURNAL.ROOT,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CASH_DISBURSEMENTS_JOURNAL_APP_ID],
    path: ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CUSTOM_ENTRIES_APP_ID],
    path: ROUTES.TRANSACTIONS.ROOT,
  },
  {
    appIds: [
      ...REPORT_WORKSPACE_APP_IDS,
      GENERAL_LEDGER_APP_ID,
      LEDGER_REPORTS_APP_ID,
    ],
    path: ROUTES.REPORTS.GENERAL_LEDGER,
  },
  {
    appIds: [...REPORT_WORKSPACE_APP_IDS, LEDGER_REPORTS_APP_ID],
    path: ROUTES.REPORTS.GENERAL_JOURNAL,
  },
  {
    appIds: [...REPORT_WORKSPACE_APP_IDS, FINANCIAL_STATEMENTS_APP_ID],
    path: ROUTES.REPORTS.TRIAL_BALANCE,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CHART_OF_ACCOUNTS_APP_ID],
    path: ROUTES.CATEGORIES.ROOT,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CUSTOMERS_APP_ID],
    path: ROUTES.JOURNALS.CUSTOMERS,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, SUPPLIERS_APP_ID],
    path: ROUTES.JOURNALS.SUPPLIERS,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, PPE_APP_ID],
    path: ROUTES.EQUIPMENT.ROOT,
  },
  {
    appIds: [...JOURNAL_WORKSPACE_APP_IDS, CAPITALS_APP_ID],
    path: ROUTES.CAPITALS.ROOT,
  },
  {
    appIds: [
      ACCOUNTING_APP_ID,
      JOURNALS_APP_ID,
      NON_VAT_ACCRUAL_BOOKS_APP_ID,
      BILLS_APP_ID,
    ],
    path: ROUTES.BILLS_LEDGER.ROOT,
  },
  {
    appIds: [ACCOUNTING_APP_ID, NON_VAT_ACCRUAL_BOOKS_APP_ID, INVOICES_APP_ID],
    path: ROUTES.AR_INVOICES.ROOT,
  },
  {
    appIds: [...BOOKS_BUNDLE_APP_IDS, UPLOAD_ENTRY_APP_ID],
    path: ROUTES.UPLOAD_ENTRY,
  },
];

/**
 * Which downloadable groups apply for the company's installed apps.
 * Matches the Downloadables page empty-state rules.
 */
export function getDownloadablesVisibility(
  isAppInstalled: (appId: string) => boolean,
  appsReady: boolean,
): DownloadablesVisibility {
  if (!appsReady) {
    return {
      showPosDownloads: false,
      showAccountingDownloads: false,
      showTaxDownloads: false,
      hasVisibleDownloads: false,
    };
  }

  const showPosDownloads = isAppInstalled(POS_APP_ID);
  const showAccountingDownloads =
    isAppInstalled(ACCOUNTING_APP_ID) ||
    isAppInstalled(JOURNALS_APP_ID) ||
    isAppInstalled(REPORTS_APP_ID) ||
    isAnySlimBooksAppInstalled(isAppInstalled);
  const showTaxDownloads = isAppInstalled(TAX_CALENDAR_APP_ID);

  return {
    showPosDownloads,
    showAccountingDownloads,
    showTaxDownloads,
    hasVisibleDownloads:
      showPosDownloads || showAccountingDownloads || showTaxDownloads,
  };
}

export function isDownloadablesPath(path: string): boolean {
  const clean = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return (
    clean === ROUTES.DOWNLOADABLES ||
    clean.startsWith(`${ROUTES.DOWNLOADABLES}/`)
  );
}

function downloadGroupAccessible(
  entries: readonly DownloadableAppAccess[],
  isAppInstalled: (appId: string) => boolean,
  canAccessPath: (path: string) => boolean,
): boolean {
  return entries.some(
    (entry) =>
      entry.appIds.some((appId) => isAppInstalled(appId)) &&
      canAccessPath(entry.path),
  );
}

/**
 * Installed downloads the user can actually open.
 * A group counts only when one of its apps is installed and `canAccessPath`
 * allows that app.
 */
export function getAccessibleDownloadablesVisibility(
  isAppInstalled: (appId: string) => boolean,
  appsReady: boolean,
  canAccessPath: (path: string) => boolean,
): DownloadablesVisibility {
  const installed = getDownloadablesVisibility(isAppInstalled, appsReady);
  const showPosDownloads =
    installed.showPosDownloads &&
    downloadGroupAccessible(
      [POS_DOWNLOAD_ACCESS],
      isAppInstalled,
      canAccessPath,
    );
  const showAccountingDownloads =
    installed.showAccountingDownloads &&
    downloadGroupAccessible(
      ACCOUNTING_DOWNLOAD_ACCESS,
      isAppInstalled,
      canAccessPath,
    );
  const showTaxDownloads =
    installed.showTaxDownloads &&
    downloadGroupAccessible(
      [TAX_DOWNLOAD_ACCESS],
      isAppInstalled,
      canAccessPath,
    );

  return {
    showPosDownloads,
    showAccountingDownloads,
    showTaxDownloads,
    hasVisibleDownloads:
      showPosDownloads || showAccountingDownloads || showTaxDownloads,
  };
}
